import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppearanceProvider, useAppearance } from "@/context/AppearanceContext";
import { isLight } from "@/lib/theme";
import AppearancePage from "@/pages/AppearancePage";
import AdminSceneBreaks from "@/pages/AdminSceneBreaks";
import AdminUpdates from "@/pages/AdminUpdates";
import { UpdatesPopup } from "@/components/UpdatesPopup";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import BillingPage from "@/pages/BillingPage";
import PaymentResult from "@/pages/PaymentResult";
import WorldLayout from "@/pages/WorldLayout";
import WorldOverview from "@/pages/WorldOverview";
import PageView from "@/pages/PageView";
import CategoryPage from "@/pages/CategoryPage";
import PageEditor from "@/pages/PageEditor";
import Timeline from "@/pages/Timeline";
import RelationshipMap from "@/pages/RelationshipMap";
import FamilyTree from "@/pages/FamilyTree";
import ShippingChart from "@/pages/ShippingChart";
import TreeOfLife from "@/pages/TreeOfLife";
import PricingPage from "@/pages/PricingPage";
import FeaturesPage from "@/pages/FeaturesPage";
import PendingChanges from "@/pages/PendingChanges";
import ActivityLog from "@/pages/ActivityLog";
import JoinPage from "@/pages/JoinPage";
import GiftPage from "@/pages/GiftPage";
import RedeemGift from "@/pages/RedeemGift";
import AdminPromos from "@/pages/AdminPromos";
import WritingDesk from "@/pages/WritingDesk";
import WorkPage from "@/pages/WorkPage";
import { Loader } from "@/components/Loader";
import { safeNext } from "@/lib/api";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
};

const GuestOnly = ({ children }) => {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  if (user === null) return <Loader />;
  if (user) return <Navigate to={safeNext(sp.get("next")) || "/worlds"} replace />;
  return children;
};

const shared = [
  <Route key="i" index element={<WorldOverview />} />,
  <Route key="p" path="p/:pageId" element={<PageView />} />,
  <Route key="c" path="c/:category" element={<CategoryPage />} />,
  <Route key="t" path="timeline" element={<Timeline />} />,
  <Route key="m" path="map" element={<RelationshipMap />} />,
  <Route key="f" path="family" element={<FamilyTree />} />,
  <Route key="s" path="chart" element={<ShippingChart />} />,
  <Route key="t" path="tree-of-life" element={<TreeOfLife />} />,
];

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<GuestOnly><Landing /></GuestOnly>} />
      <Route path="/login" element={<GuestOnly><AuthPage mode="login" /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><AuthPage mode="register" /></GuestOnly>} />
      <Route path="/worlds" element={<Protected><Dashboard /></Protected>} />
      <Route path="/writing" element={<Protected><WritingDesk /></Protected>} />
      <Route path="/writing/:workId" element={<Protected><WorkPage /></Protected>} />
      <Route path="/billing" element={<Protected><BillingPage /></Protected>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/invite/:token" element={<JoinPage mode="invite" />} />
      <Route path="/join/:token" element={<JoinPage mode="link" />} />
      <Route path="/gift" element={<Protected><GiftPage /></Protected>} />
      <Route path="/gift/redeem/:code" element={<RedeemGift />} />
      <Route path="/admin/promos" element={<Protected><AdminPromos /></Protected>} />
      <Route path="/admin/scene-breaks" element={<Protected><AdminSceneBreaks /></Protected>} />
      <Route path="/admin/updates" element={<Protected><AdminUpdates /></Protected>} />
      <Route path="/settings/appearance" element={<Protected><AppearancePage /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentResult success /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentResult /></Protected>} />
      <Route path="/worlds/:worldId" element={<Protected><WorldLayout mode="owner" /></Protected>}>
        {shared}
        <Route path="new" element={<PageEditor />} />
        <Route path="pending" element={<PendingChanges />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="p/:pageId/edit" element={<PageEditor />} />
      </Route>
      <Route path="/worlds/:worldId/fanon/:profileId" element={<Protected><WorldLayout mode="owner" /></Protected>}>
        {shared}
        <Route path="new" element={<PageEditor />} />
        <Route path="pending" element={<PendingChanges />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="p/:pageId/edit" element={<PageEditor />} />
      </Route>
      <Route path="/share/:token" element={<WorldLayout mode="public" />}>{shared}</Route>
      <Route path="/share/:token/fanon/:profileId" element={<WorldLayout mode="public" />}>{shared}</Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const ThemedToaster = () => {
  const { active } = useAppearance();
  return <Toaster theme={active && isLight(active) ? "light" : "dark"} position="bottom-right"
    toastOptions={{ style: { background: "rgb(var(--c-panel))", border: "1px solid rgb(var(--c-line))", color: "rgb(var(--slate-100))" } }} />;
};

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <AppearanceProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <UpdatesPopup />
          <ThemedToaster />
        </AppearanceProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
